import { LIVE_MS, formatSpend } from "./format";
import { nextLocalMidnight } from "./useNow";

// convex/observability/lib.ts `SessionView`: what listSessions and getSession
// return for one session. It carries the Steerly ref (the bare terminal or
// chat id), which the session page shows as text, and never the session key,
// which no page may parse.
export interface SessionView {
  id: string;
  userId: string;
  tool: string;
  toolName: string;
  toolVersion: string | null;
  source: string;
  sourceKind: string;
  steerlyRef: string | null;
  repo: string | null;
  gitBranch: string | null;
  cwd: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
  endedAt: number | null;
  stoppedAt: number | null;
  stoppedBy: string | null;
  routed: boolean;
  notRoutedReason: string | null;
  models: string[];
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costMicroUsd: number;
  unpricedCalls: number;
  toolCalls: number;
  dlpFindings: number;
  errorCalls: number;
  flagged: boolean;
}

type Costed = Pick<SessionView, "calls" | "unpricedCalls" | "costMicroUsd">;

// A session's cost at API list prices, written as the AI overview writes
// spend (format.ts formatSpend): Convex adds up only the calls it could price,
// so a session whose calls are all unpriced reads "not priced", never "$0.00";
// one with some unpriced calls says how many ("$1.23 + 1 call not priced");
// one with no calls has no cost Steerly saw.
export function sessionCost(s: Costed): string {
  return formatSpend(s.costMicroUsd, s.calls, s.unpricedCalls);
}

// Every reason a session is flagged and nothing else (Convex's `flagged`: DLP
// findings, or not routed), so a flagged row never has an empty Flags cell and
// the Flagged filter keeps every row the column flags. Errors are not a flag
// (spec section 8): they show with the calls (sessionErrors).
export function sessionFlags(s: Pick<SessionView, "routed" | "dlpFindings">): string[] {
  return [s.routed ? null : "not routed", s.dlpFindings > 0 ? `${s.dlpFindings} DLP` : null].filter(
    (f): f is string => f !== null
  );
}

// What a list's Calls cell adds after the count for a session with errors:
// " (1 error)", " (2 errors)", else nothing.
export function sessionErrors(s: Pick<SessionView, "errorCalls">): string {
  if (!(s.errorCalls > 0)) return "";
  return ` (${s.errorCalls} ${s.errorCalls === 1 ? "error" : "errors"})`;
}

// The next moment a session shown changes on its own, with no new data: a live
// one turning idle (LIVE_MS after it was last seen, sessionStatus), or local
// midnight, when a time shown as today's gains its date (formatWhen). For
// useNow.
export function nextSessionChange(
  sessions: ReadonlyArray<Pick<SessionView, "lastSeenAt" | "endedAt" | "stoppedAt">>,
  now: number
): number {
  let next = nextLocalMidnight(now);
  for (const s of sessions) {
    if (s.stoppedAt || s.endedAt) continue;
    const idleAt = s.lastSeenAt + LIVE_MS;
    if (idleAt > now && idleAt < next) next = idleAt;
  }
  return next;
}
